// ManagerEntitiesMenu.tsx
import React, { useState } from "react";
import { Typography, MenuItem, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ManagerUserModal from "./ManagerUserModal";
import ManagerEntitiesModal from "./ManagerEntitiesModal"; // Importar o novo modal
import { getAllUsersAndFoldersPermissions } from "../api"; // Ajuste o caminho conforme necessário
import CircularProgress from "@mui/material/CircularProgress";

interface Permission {
  folderPath: string;
  accessPermission: boolean;
}

interface UserFolderData {
  userId: number; // Certifique-se de que o backend retorna esse campo
  login: string;
  permissions: Permission[];
  fullname: string; // Certifique-se de que fullname esteja incluído
}

interface ManagerEntitiesMenuProps {
  fetchUsers: (login: string) => Promise<void>; // Tipo da função fetchUsers
  login: string; // Tipo do login
}

const ManagerEntitiesMenu: React.FC<ManagerEntitiesMenuProps> = ({
  fetchUsers,
  login,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEntitiesModalOpen, setIsEntitiesModalOpen] = useState(false); // Novo estado para o modal de entidades
  const [userFoldersData, setUserFoldersData] = useState<UserFolderData[]>([]);
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false); // Loading global


  // Abrir modal de usuários e buscar dados
  const handleOpenUserModal = async () => {
    try {
      setIsLoading(true); // Inicia loading
      const storedUser = sessionStorage.getItem("user");
      if (!storedUser) throw new Error("Usuário não encontrado no sessionStorage");
  
      const { login } = JSON.parse(storedUser);
      const data = await getAllUsersAndFoldersPermissions(login);
  
      const mappedData = data.map((item: any) => ({
        ...item,
        fullname: item.fullName || "N/A",
      }));
  
      setUserFoldersData(mappedData);
      setIsUserModalOpen(true);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setIsLoading(false); // Finaliza loading
    }
  };
  

  // Abrir modal de entidades (inicialmente em branco)
const handleOpenEntitiesModal = () => {
  setIsEntitiesModalOpen(true);
};


  // Fechar modais
  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
  };

  const handleCloseEntitiesModal = () => {
    setIsEntitiesModalOpen(false);
  };



  return (
    <>
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 10000,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="primary" size={60} />
        </Box>
      )}
  
      <Box
        onMouseEnter={() => !isUserModalOpen && !isEntitiesModalOpen && setIsMenuOpen(true)}
        onMouseLeave={() => setIsMenuOpen(false)}
        sx={{
          position: "relative",
          display: "inline-block",
          zIndex: 300,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.text.primary,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Gerenciamento
        </Typography>
  
        {isMenuOpen && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 250,
              backgroundColor:
                theme.palette.mode === "dark" ? "#424242" : "#d4eaff",
              color: theme.palette.text.primary,
              borderColor:
                theme.palette.mode === "dark" ? "#616161" : "#b0bec5",
              borderRadius: 1,
              boxShadow: "0px 6px 10px rgba(0,0,0,0.5)",
              minWidth: "120px",
              overflow: "hidden",
            }}
          >
            <MenuItem
              onClick={() => {
                handleOpenUserModal();
                setIsMenuOpen(false);
              }}
              sx={{ color: theme.palette.text.primary }}
            >
              Usuários
            </MenuItem>
  
            <MenuItem
              onClick={() => {
                handleOpenEntitiesModal();
                setIsMenuOpen(false);
              }}
              sx={{ color: theme.palette.text.primary }}
            >
              Entidades
            </MenuItem>
          </Box>
        )}
  
        {isUserModalOpen && (
          <ManagerUserModal
            open={isUserModalOpen}
            onClose={handleCloseUserModal}
            userFoldersData={userFoldersData}
            setUserFoldersData={setUserFoldersData}
            refreshAdminUsers={() => fetchUsers(login)}
          />
        )}
  
        {isEntitiesModalOpen && (
          <ManagerEntitiesModal
            open={isEntitiesModalOpen}
            onClose={handleCloseEntitiesModal}
          />
        )}
      </Box>
    </>
  );
};

export default ManagerEntitiesMenu;
